/**
 * @spell/spell-schema
 *
 * The on-disk / on-the-wire spell format. Deliberately a plain JSON document:
 * no browser objects, no React components, no Three.js types. A different
 * runtime (e.g. an Unreal port) can consume the exact same payload and
 * re-interpret `effects` + `stats` with its own renderer.
 */
import { z } from 'zod';
import {
  EFFECT_MODULES,
  PROJECTILE_SHAPES,
  SPELL_CATEGORIES,
  SPELL_ELEMENTS,
  SPELL_SCHEMA_VERSION,
  SPELL_TYPES,
  VFX_TAGS,
} from '@spell/shared-types';

/* ------------------------------------------------------------------ */
/* Gesture                                                             */
/* ------------------------------------------------------------------ */

export const gesturePointSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  t: z.number().finite().nonnegative(),
  s: z.number().int().min(0).max(15),
});
export type GesturePointData = z.infer<typeof gesturePointSchema>;

export const rawGestureSchema = z
  .array(gesturePointSchema)
  .min(4, 'A gesture needs at least 4 sampled points')
  .max(2048);
export type RawGesture = z.infer<typeof rawGestureSchema>;

/**
 * A normalised gesture template: position/scale invariant, fixed point count,
 * ready for direct comparison. Produced by @spell/gesture-engine.
 */
export const gestureTemplateSchema = z.object({
  /** Resampled + normalised points, always `resampleCount` long. */
  points: z.array(z.object({ x: z.number(), y: z.number(), s: z.number().int() })),
  resampleCount: z.number().int().positive(),
  strokeCount: z.number().int().min(1).max(16),
  /** Turning angle profile used for curvature comparison. */
  turning: z.array(z.number()),
  /** Total path length in normalised units, before scaling. */
  rawScale: z.number().nonnegative(),
  /** Agreement between the two registration samples, 0..1. */
  selfConsistency: z.number().min(0).max(1),
});
export type GestureTemplate = z.infer<typeof gestureTemplateSchema>;

/* ------------------------------------------------------------------ */
/* Effects                                                             */
/* ------------------------------------------------------------------ */

export const effectModuleSchema = z.enum(EFFECT_MODULES);

/**
 * What the AI (or the local interpreter) is allowed to emit. Intensity is a
 * coarse 0..1 dial the balance system reads — it is *not* a damage number.
 */
export const effectSpecSchema = z.object({
  module: effectModuleSchema,
  intensity: z.number().min(0).max(1).default(0.5),
});
export type EffectSpec = z.infer<typeof effectSpecSchema>;

/* ------------------------------------------------------------------ */
/* Derived, rules-owned numbers                                        */
/* ------------------------------------------------------------------ */

export const spellStatsSchema = z.object({
  damage: z.number().min(0),
  /** Damage per second for DamageOverTime, 0 when unused. */
  dotDps: z.number().min(0),
  dotDurationMs: z.number().min(0),
  healAmount: z.number().min(0),
  shieldAmount: z.number().min(0),
  /** Fraction of a blocked attack that is sent back, 0..1. */
  reflectRatio: z.number().min(0).max(1),
  /** Fraction of a blocked attack converted into mana, 0..1. */
  absorbRatio: z.number().min(0).max(1),
  stunMs: z.number().min(0),
  slowMs: z.number().min(0),
  slowFactor: z.number().min(0).max(1),
  knockback: z.number().min(0),
  /** Multiplier applied to the caster's next offensive spell. */
  nextSpellMultiplier: z.number().min(1),
  radius: z.number().min(0),
  speed: z.number().min(0),
  /**
   * How many projectiles one cast sends, and how far apart.
   *
   * A derived number like every other stat here: the player describes a volley
   * in prose, the rules decide how many shots that is worth. Defaulted so a
   * spell stored before volleys existed still parses as the single shot it was.
   */
  projectileCount: z.number().int().min(1).max(8).default(1),
  burstIntervalMs: z.number().min(0).default(0),
  durationMs: z.number().min(0),
  manaCost: z.number().min(0),
  cooldownMs: z.number().min(0),
  castTimeMs: z.number().min(0),
  /** Whether the *opponent* can parry this spell. */
  parryable: z.boolean(),
  /** Homing strength 0..1; 0 means a straight projectile. */
  homing: z.number().min(0).max(1),
  /** Budget bookkeeping so the result screen can explain a spell. */
  budgetUsed: z.number().min(0),
  budgetTotal: z.number().min(0),
  potencyScale: z.number().min(0).max(1),
});
export type SpellStats = z.infer<typeof spellStatsSchema>;

/* ------------------------------------------------------------------ */
/* Presentation                                                        */
/* ------------------------------------------------------------------ */

export const spellVisualsSchema = z.object({
  /** Primary colour, `#rrggbb`. */
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  /** Secondary / rim colour. */
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  element: z.enum(SPELL_ELEMENTS),
  projectileShape: z.enum(PROJECTILE_SHAPES),
  vfxTags: z.array(z.enum(VFX_TAGS)).max(4),
  /** How busy the magic circle is; drives ring/rune counts. */
  circleComplexity: z.number().int().min(1).max(4),
});
export type SpellVisuals = z.infer<typeof spellVisualsSchema>;

/* ------------------------------------------------------------------ */
/* The spell document                                                  */
/* ------------------------------------------------------------------ */

export const spellSchema = z.object({
  schemaVersion: z.literal(SPELL_SCHEMA_VERSION),
  id: z.string().min(3).max(64),
  ownerId: z.string().min(1).max(64),
  type: z.enum(SPELL_TYPES),
  category: z.enum(SPELL_CATEGORIES),

  /**
   * Exactly what the player typed/said, preserved for display. Empty for the
   * shared basic bolt, which is fired with a click and has no incantation;
   * `spellDraftSchema` is where a *crafted* spell's chant is required.
   */
  chant: z.string().max(48),
  /** Upper-cased, punctuation-stripped, single-spaced. Used for matching. */
  normalizedChant: z.string().min(1).max(48),
  /** The player's own free-form wish. Never executed, only interpreted. */
  description: z.string().max(400),
  /** One sentence shown back to the player after interpretation. */
  summary: z.string().max(200),
  /** Coarse text the *opponent* is allowed to see. */
  publicSummary: z.string().max(120),

  gesture: gestureTemplateSchema.nullable(),
  effects: z.array(effectSpecSchema).min(1).max(6),
  visuals: spellVisualsSchema,
  stats: spellStatsSchema,

  balanceVersion: z.string(),
  /** 'ai' when a model produced the interpretation, 'local' for the fallback. */
  interpreter: z.enum(['ai', 'local']),
  createdAt: z.number().int().nonnegative(),
});
export type Spell = z.infer<typeof spellSchema>;

/** What the opponent is allowed to learn about a spell. */
export const spellPublicSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  type: z.enum(SPELL_TYPES),
  category: z.enum(SPELL_CATEGORIES),
  chant: z.string(),
  publicSummary: z.string(),
  color: z.string(),
  element: z.enum(SPELL_ELEMENTS),
});
export type SpellPublic = z.infer<typeof spellPublicSchema>;

export function toPublicSpell(spell: Spell): SpellPublic {
  return {
    id: spell.id,
    ownerId: spell.ownerId,
    type: spell.type,
    category: spell.category,
    chant: spell.chant,
    publicSummary: spell.publicSummary,
    color: spell.visuals.color,
    element: spell.visuals.element,
  };
}

/* ------------------------------------------------------------------ */
/* Craft request (what a player submits)                               */
/* ------------------------------------------------------------------ */

export const spellDraftSchema = z.object({
  type: z.enum(['parry', 'signature']),
  chant: z.string().min(1).max(48),
  description: z.string().min(1).max(400),
  /** Two registrations of the same gesture. */
  gestureSamples: z.array(rawGestureSchema).min(1).max(2),
});
export type SpellDraft = z.infer<typeof spellDraftSchema>;

/**
 * The structured object an AI model must return. Notice it contains *no*
 * damage, mana or cooldown values — those are the rules system's job.
 */
export const interpretationSchema = z.object({
  category: z.enum(SPELL_CATEGORIES),
  element: z.enum(SPELL_ELEMENTS),
  projectileShape: z.enum(PROJECTILE_SHAPES),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  vfxTags: z.array(z.enum(VFX_TAGS)).min(1).max(4),
  circleComplexity: z.number().int().min(1).max(4),
  effects: z.array(effectSpecSchema).min(1).max(6),
  summary: z.string().min(1).max(200),
  publicSummary: z.string().min(1).max(120),
  /**
   * Set when the player asked for something absolute ("blocks absolutely
   * everything"). The rules system answers by stripping side effects and
   * inflating cost rather than by refusing the request.
   */
  unconditional: z.boolean().default(false),
  /**
   * Set when the player asked for many small shots rather than one big one
   * ("여러 발", "산탄", "빗발치는"). Like `unconditional`, this is a statement
   * about *shape*, not about numbers: how many shots that becomes, how the
   * damage divides and what it costs are the balance table's to decide.
   */
  volley: z.boolean().default(false),
});
export type SpellInterpretation = z.infer<typeof interpretationSchema>;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Chant normalisation shared by crafting and by live speech matching. */
export function normalizeChant(raw: string): string {
  return raw
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s'-]/g, ' ')
    .replace(/[\s'-]+/g, ' ')
    .trim()
    .toUpperCase();
}

export function chantWordCount(normalized: string): number {
  if (!normalized) return 0;
  return normalized.split(' ').filter(Boolean).length;
}

/** Rough syllable estimate — good enough to price a chant's length. */
export function chantSyllables(normalized: string): number {
  const words = normalized.split(' ').filter(Boolean);
  let total = 0;
  for (const w of words) {
    const groups = w.toLowerCase().replace(/[^a-z]/g, '').match(/[aeiouy]+/g);
    total += Math.max(1, groups ? groups.length : 1);
  }
  return total;
}

export function safeParseSpell(value: unknown) {
  return spellSchema.safeParse(value);
}
